import * as React from "react";
import cx from "classnames";

import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import {
  DraggableList,
  DraggableListDataItem,
  DraggableListDirection,
  DraggableListProps,
} from "@common/components/draggable-list";

import "./tabs.styles.scss";

export type TabsItem = {
  id: number | string;
  name: string;
};

export type TabsItemOnOrderChange = (tabs: TabsItem[]) => void;

export type TabsProps = {
  className?: string;
  items: Array<TabsItem>;
  onChange: (tabId: TabsItem["id"]) => void;
  onTabOrderChange: TabsItemOnOrderChange;
  value: TabsItem["id"];
  classes?: {
    tabs?: string;
  };
};

export const Tabs: React.FC<TabsProps> = (props) => {
  const { items, className, value, onChange, classes, onTabOrderChange } = props;

  const [showLeftButton, setShowLeftButton] = React.useState(true);
  const [showRightButton, setShowRightButton] = React.useState(true);
  const sliderRef = React.useRef<HTMLDivElement>(null);

  const horizontalSlidePx = 100;

  const checkButtonsVisibility = () => {
    const slider = sliderRef.current;
    if (slider) {
      setShowLeftButton(slider.scrollLeft > 0);
      setShowRightButton(slider.scrollWidth > (slider.clientWidth + Math.ceil(slider.scrollLeft)));
    }
  };

  const sliderBtnAttrKey = "data-slider-btn";
  const sliderLeftBtnAttrValue = "left";
  const sliderRightBtnAttrValue = "right";

  const setSliderBtnAttr = React.useCallback(
    (position: typeof sliderLeftBtnAttrValue | typeof sliderRightBtnAttrValue) => {
      return {
        [sliderBtnAttrKey]: position,
      };
    },
    []
  );

  React.useEffect(() => {
    checkButtonsVisibility();
    window.addEventListener("resize", checkButtonsVisibility);
    return () => window.removeEventListener("resize", checkButtonsVisibility);
  }, []);

  const scrollLeft = React.useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -horizontalSlidePx, behavior: "smooth" });
      setTimeout(checkButtonsVisibility, 300);
    }
  }, [sliderRef.current]);

  const scrollRight = React.useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: horizontalSlidePx, behavior: "smooth" });
      setTimeout(checkButtonsVisibility, 300);
    }
  }, [sliderRef.current]);

  const handleClickTab = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      const clickedTab = (e.target as HTMLElement);
      clickedTab.scrollIntoView({ behavior: "smooth" });
      const id =  clickedTab.dataset.customTabId as string;
      onChange(id);
    },
    [onChange]
  );

  const renderItem = React.useCallback(
    (tab: DraggableListDataItem) => {
      return (
        <div
          className={cx("DatasheetsEditorDatasheetTabs__tab", {
            ["DatasheetsEditorDatasheetTabs__tab--active"]: value == tab.id,
          })}
          key={tab.id}
          onClick={handleClickTab}
          data-custom-tab-id={tab.id}
        >
          {tab.name}
        </div>
      );
    },
    [handleClickTab, value]
  );

  const handleSelectDraggingTab = (list: Array<TabsItem>, tab: TabsItem) => {
    onTabOrderChange(list);

    const selectMovedTab = value !== tab.id;

    if (selectMovedTab) {
      onChange(tab.id);
    }

    const someGapForTriggerSlider = 200;

    const movedTabLeftPosition =
      document.querySelector(`[data-custom-tab-id="${tab.id}"]`)?.getBoundingClientRect().left || 0;

    const movedTabRightPosition =
      movedTabLeftPosition +
      (document.querySelector(`[data-custom-tab-id="${tab.id}"]`)?.getBoundingClientRect().width || 0);

    const sliderLeftBtnPosition =
      document.querySelector(`[${sliderBtnAttrKey}="${sliderLeftBtnAttrValue}"]`)?.getBoundingClientRect().left || 0;

    const sliderRightBtnPosition =
      document.querySelector(`[${sliderBtnAttrKey}="${sliderRightBtnAttrValue}"]`)?.getBoundingClientRect().left || 0;

    if (movedTabRightPosition + someGapForTriggerSlider >= sliderRightBtnPosition) {
      scrollRight();
    } else if (sliderLeftBtnPosition + someGapForTriggerSlider >= movedTabLeftPosition) {
      scrollLeft();
    }
  };

  return (
    <Flex
      className={cx("DatasheetsEditorDatasheetTabs", className)}
      width="100%"
      justify="space-between"
      alignItems="flex-end"
    >
      <Button
        className={cx("DatasheetsEditorDatasheetTabs__slider-button", {
          ["DatasheetsEditorDatasheetTabs__slider-button--active"]: showLeftButton,
        })}
        disabled={!showLeftButton}
        onClick={scrollLeft}
        {...setSliderBtnAttr(sliderLeftBtnAttrValue)}
      >
        <i className="fa fa-chevron-left"></i>
      </Button>
      <div className="DatasheetsEditorDatasheetTabs__slider" ref={sliderRef}>
        <DraggableList
          className={cx("DatasheetsEditorDatasheetTabs__slider__items", classes?.tabs)}
          classes={{
            item: "DatasheetsEditorDatasheetTabs__slider__item",
          }}
          items={items}
          renderItem={renderItem as DraggableListProps["renderItem"]}
          onChange={handleSelectDraggingTab as DraggableListProps["onChange"]}
          direction={DraggableListDirection.horizontal}
        />
      </div>
      <Button
        className={cx("DatasheetsEditorDatasheetTabs__slider-button", {
          ["DatasheetsEditorDatasheetTabs__slider-button--active"]: showRightButton,
        })}
        disabled={!showRightButton}
        onClick={scrollRight}
        {...setSliderBtnAttr(sliderRightBtnAttrValue)}
      >
        <i className="fa fa-chevron-right"></i>
      </Button>
    </Flex>
  );
};
